import { useEffect, useState, useCallback, useMemo } from "react";
import { useToast } from "../../components/ui/Toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import {
  Plus,
  GraduationCap,
  Clock,
  Trash,
  WifiSlash,
  ArrowClockwise,
  CaretRight,
  SquaresFour,
  List,
  MagnifyingGlass,
  SortAscending,
  WarningCircle,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useHomeworkStore } from "../../stores/homework";
import type { CourseListItem } from "../../types/homework";

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };
const springFast = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };
const stagger = { staggerChildren: 0.04, delayChildren: 0.06 };
const itemVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: spring },
};

function getRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function ViewToggle({ view, onChange }: { view: "grid" | "list"; onChange: (v: "grid" | "list") => void }) {
  return (
    <div className="relative flex items-center rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-[var(--bg-base)] p-0.5">
      <motion.div
        layout
        layoutId="class-view-pill"
        className="absolute top-0.5 bottom-0.5 rounded-[var(--radius-sm)] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
        style={{ left: view === "grid" ? 2 : "50%", width: "calc(50% - 2px)" }}
        transition={springFast}
      />
      {(["grid", "list"] as const).map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={cn(
            "relative z-[1] flex items-center justify-center gap-1 rounded-[var(--radius-sm)] px-2.5 py-1 text-[11px] font-medium",
            "transition-colors duration-100",
            view === v ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]",
          )}
        >
          {v === "grid" ? <SquaresFour size={12} weight="bold" /> : <List size={12} weight="bold" />}
          {v === "grid" ? "Grid" : "List"}
        </button>
      ))}
    </div>
  );
}

function CourseCard({
  course,
  onClick,
  onDelete,
}: {
  course: CourseListItem;
  onClick: () => void;
  onDelete: () => void;
}) {
  const date = new Date(course.updated_at || course.created_at);
  const relative = getRelativeTime(date);

  return (
    <motion.div
      variants={itemVariants}
      layout
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={springFast}
      className={cn(
        "group relative flex flex-col items-start rounded-[var(--radius-lg)] p-3.5",
        "border border-[var(--border-hairline)]",
        "bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-hover)]",
        "hover:border-[var(--border-subtle)] hover:shadow-[var(--shadow-sm)]",
        "cursor-pointer text-left",
      )}
    >
      <div className="flex items-center justify-between w-full mb-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] bg-[var(--bg-base)] text-[var(--text-muted)]">
          <GraduationCap size={15} weight="fill" />
        </div>
        <motion.button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.85 }}
          className={cn(
            "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
            "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
            "hover:bg-[var(--color-soft-red)]/10 hover:text-[var(--color-soft-red)]",
            "transition-all duration-100",
          )}
          aria-label="Delete course"
        >
          <Trash size={13} />
        </motion.button>
      </div>
      <div className="w-full min-w-0">
        <div className="text-[13px] font-semibold text-[var(--text-primary)] truncate leading-tight">
          {course.name}
        </div>
        <div className="text-[11px] text-[var(--text-muted)] truncate mt-0.5 leading-tight">
          {[course.instructor, course.term].filter(Boolean).join(" · ") || "No details"}
        </div>
      </div>
      <div className="flex items-center justify-between w-full mt-2.5">
        <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
          <Clock size={10} />
          {relative}
        </span>
        <CaretRight
          size={12}
          className="text-[var(--text-muted)] opacity-0 group-hover:opacity-60 transition-opacity duration-100"
        />
      </div>
    </motion.div>
  );
}

function CourseRow({
  course,
  onClick,
  onDelete,
}: {
  course: CourseListItem;
  onClick: () => void;
  onDelete: () => void;
}) {
  const date = new Date(course.updated_at || course.created_at);
  const relative = getRelativeTime(date);

  return (
    <motion.div
      variants={itemVariants}
      layout
      onClick={onClick}
      transition={springFast}
      className={cn(
        "group flex items-center gap-3 w-full px-3.5 py-2.5 text-left",
        "border-b border-[var(--border-hairline)] last:border-b-0",
        "hover:bg-[var(--bg-surface-hover)]",
        "active:bg-[var(--bg-base)]",
        "transition-colors duration-75 cursor-pointer",
      )}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--bg-base)] text-[var(--text-muted)]">
        <GraduationCap size={14} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-medium text-[var(--text-primary)] truncate block leading-tight">
          {course.name}
        </span>
        <span className="text-[11px] text-[var(--text-muted)] truncate block leading-tight">
          {[course.instructor, course.term].filter(Boolean).join(" · ") || "No details"}
        </span>
      </div>
      <span className="flex items-center gap-1 text-[11px] text-[var(--text-muted)] shrink-0">
        <Clock size={11} />
        {relative}
      </span>
      <motion.button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.85 }}
        className={cn(
          "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
          "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
          "hover:bg-[var(--color-soft-red)]/10 hover:text-[var(--color-soft-red)]",
          "transition-all duration-100",
        )}
        aria-label="Delete course"
      >
        <Trash size={13} />
      </motion.button>
    </motion.div>
  );
}

function CreateCourseDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [instructor, setInstructor] = useState("");
  const [term, setTerm] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { createCourse } = useHomeworkStore();

  const handleCreate = useCallback(async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const course = await createCourse(
        name.trim(),
        undefined,
        instructor.trim() || undefined,
        term.trim() || undefined,
      );
      navigate(`/classes/${course.id}`);
      onClose();
      setName("");
      setInstructor("");
      setTerm("");
    } catch {
      setError("Failed to create class. Please try again.");
    } finally {
      setCreating(false);
    }
  }, [name, instructor, term, createCourse, navigate, onClose, creating]);

  if (!open) return null;

  const inputClasses = cn(
    "w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
    "bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)]",
    "placeholder:text-[var(--text-muted)]",
    "outline-none focus:border-[var(--color-lever-orange)]",
    "transition-colors duration-150",
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className={cn(
              "relative z-10 w-full max-w-sm",
              "rounded-[var(--radius-xl)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-xl)]",
              "p-5",
            )}
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={spring}
          >
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-display)" }}>
              Add Class
            </h2>

            <div className="mt-3.5 flex flex-col gap-2.5">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                  Class Name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. ECON 101"
                  autoFocus
                  className={inputClasses}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                    Instructor
                  </label>
                  <input
                    value={instructor}
                    onChange={(e) => setInstructor(e.target.value)}
                    placeholder="Prof. Smith"
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                    Term
                  </label>
                  <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Spring 2026"
                    className={inputClasses}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  />
                </div>
              </div>
            </div>

            {error && (
              <p className="mt-3 text-[11px] text-[var(--color-soft-red)]">{error}</p>
            )}

            <div className="mt-4 flex justify-end gap-1.5">
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)]",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "transition-colors duration-100",
                )}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleCreate}
                disabled={!name.trim() || creating}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3.5 py-1.5 text-[12px] font-semibold",
                  "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                  "disabled:opacity-30 disabled:cursor-not-allowed",
                  "transition-opacity duration-100",
                )}
              >
                {creating ? "Creating..." : "Create"}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={spring}
      className="flex flex-col items-center justify-center py-12 text-center"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
        <WifiSlash size={18} className="text-[var(--color-soft-red)]" />
      </div>
      <p className="mt-2.5 text-[13px] font-medium text-[var(--text-primary)]">Can't reach the server</p>
      <p className="mt-0.5 max-w-xs text-[11px] text-[var(--text-muted)]">{message}</p>
      <motion.button
        onClick={onRetry}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className={cn(
          "mt-3 flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium",
          "border border-[var(--border-default)] text-[var(--text-primary)]",
          "hover:bg-[var(--bg-surface-hover)]",
          "transition-colors duration-100",
        )}
      >
        <ArrowClockwise size={12} />
        Retry
      </motion.button>
    </motion.div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={spring}
      className="flex flex-col items-center justify-center py-12 text-center"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-base)]">
        <GraduationCap size={18} weight="fill" className="text-[var(--text-muted)]" />
      </div>
      <p className="mt-2.5 text-[13px] font-medium text-[var(--text-primary)]">No classes yet</p>
      <p className="mt-0.5 max-w-xs text-[11px] text-[var(--text-muted)]">Add a class to start tracking assignments.</p>
      <motion.button
        onClick={onCreate}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className={cn(
          "mt-3 flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-semibold",
          "bg-[var(--text-primary)] text-[var(--text-inverse)]",
          "transition-opacity duration-100",
        )}
      >
        <Plus size={12} weight="bold" />
        Add class
      </motion.button>
    </motion.div>
  );
}

function LoadingSkeletonGrid() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex flex-col gap-2.5 rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] p-3.5 animate-pulse">
          <div className="h-8 w-8 rounded-[var(--radius-md)] bg-[var(--bg-base)]" />
          <div className="space-y-1">
            <div className="h-3.5 w-3/4 rounded bg-[var(--bg-base)]" />
            <div className="h-2.5 w-1/2 rounded bg-[var(--bg-base)]" />
          </div>
          <div className="h-2.5 w-16 rounded bg-[var(--bg-base)] mt-auto" />
        </div>
      ))}
    </div>
  );
}

type SortKey = "name" | "updated" | "created";
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "updated", label: "Last updated" },
  { key: "name", label: "Name" },
  { key: "created", label: "Date created" },
];

function DeleteConfirmDialog({
  open,
  courseName,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  courseName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className={cn(
              "relative z-10 w-full max-w-sm",
              "rounded-[var(--radius-xl)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-xl)] p-5",
            )}
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={spring}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
                <WarningCircle size={18} className="text-[var(--color-soft-red)]" />
              </div>
              <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">
                Delete class?
              </h2>
            </div>
            <p className="text-[12px] text-[var(--text-muted)] leading-relaxed mb-4">
              <strong className="text-[var(--text-secondary)]">{courseName}</strong> and all its
              assignments will be permanently deleted. This cannot be undone.
            </p>
            <div className="flex justify-end gap-1.5">
              <motion.button
                onClick={onCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)]",
                  "hover:bg-[var(--bg-surface-hover)] transition-colors duration-100",
                )}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={onConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3.5 py-1.5 text-[12px] font-semibold",
                  "bg-[var(--color-soft-red)] text-white",
                  "transition-opacity duration-100",
                )}
              >
                Delete
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SortDropdown({ value, onChange }: { value: SortKey; onChange: (k: SortKey) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find((o) => o.key === value)!;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[11px] font-medium",
          "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
          "border border-[var(--border-hairline)] transition-colors duration-100",
        )}
      >
        <SortAscending size={12} weight="bold" />
        {current.label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={cn(
              "absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden",
              "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-lg)] py-1",
            )}
          >
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => { onChange(opt.key); setOpen(false); }}
                className={cn(
                  "flex w-full items-center px-3 py-1.5 text-[11px]",
                  "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
                  opt.key === value
                    ? "text-[var(--text-primary)] font-semibold"
                    : "text-[var(--text-secondary)]",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ClassesDashboard() {
  useDocumentTitle("Classes");
  const { courses, isLoading, fetchError, fetchCourses, deleteCourse } = useHomeworkStore();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(searchParams.get("create") === "true");
  const [hasFetched, setHasFetched] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [deleteTarget, setDeleteTarget] = useState<CourseListItem | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses().finally(() => setHasFetched(true));
  }, [fetchCourses]);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreate(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filteredCourses = useMemo(() => {
    let list = [...courses];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.instructor?.toLowerCase().includes(q) ||
          c.term?.toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name);
      if (sortKey === "created")
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return (
        new Date(b.updated_at || b.created_at).getTime() -
        new Date(a.updated_at || a.created_at).getTime()
      );
    });
    return list;
  }, [courses, searchQuery, sortKey]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteCourse(deleteTarget.id);
      toast.success("Class deleted", `"${deleteTarget.name}" has been removed.`);
    } catch {
      toast.error("Delete failed", "Could not delete the class. Please try again.");
    }
    setDeleteTarget(null);
  }, [deleteTarget, deleteCourse, toast]);

  const showLoading = !hasFetched && isLoading;
  const showError = hasFetched && fetchError;
  const showEmpty = hasFetched && !fetchError && courses.length === 0;
  const showCourses = hasFetched && !fetchError && courses.length > 0;

  return (
    <LayoutGroup>
      <div className="p-4 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1
              className="text-[17px] font-semibold text-[var(--text-primary)] tracking-[-0.03em]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Classes
            </h1>
            <p className="text-[11px] text-[var(--text-muted)]">
              {showCourses ? `${courses.length} course${courses.length !== 1 ? "s" : ""}` : "Courses, syllabi, and assignments"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <motion.button
              onClick={() => setShowCreate(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-semibold",
                "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                "transition-opacity duration-100",
              )}
            >
              <Plus size={13} weight="bold" />
              Add class
            </motion.button>
          </div>
        </div>

        {/* Search + Sort bar */}
        {showCourses && (
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlass
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search classes..."
                className={cn(
                  "w-full rounded-[var(--radius-md)] border border-[var(--border-hairline)]",
                  "bg-[var(--bg-base)] pl-8 pr-3 py-1.5 text-[12px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)]",
                  "outline-none focus:border-[var(--border-default)]",
                  "transition-colors duration-100",
                )}
              />
            </div>
            <SortDropdown value={sortKey} onChange={setSortKey} />
          </div>
        )}

        {/* Content */}
        {showLoading && <LoadingSkeletonGrid />}

        {showError && (
          <ErrorState
            message={fetchError}
            onRetry={() => {
              setHasFetched(false);
              fetchCourses().finally(() => setHasFetched(true));
            }}
          />
        )}

        {showEmpty && <EmptyState onCreate={() => setShowCreate(true)} />}

        {showCourses && filteredCourses.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <MagnifyingGlass size={18} className="text-[var(--text-muted)]" />
            <p className="mt-2 text-[13px] text-[var(--text-muted)]">No classes matching "{searchQuery}"</p>
          </div>
        )}

        {showCourses && filteredCourses.length > 0 && viewMode === "grid" && (
          <motion.div
            variants={{ show: { transition: stagger } }}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5"
          >
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onClick={() => navigate(`/classes/${course.id}`)}
                onDelete={() => setDeleteTarget(course)}
              />
            ))}
          </motion.div>
        )}

        {showCourses && filteredCourses.length > 0 && viewMode === "list" && (
          <motion.div
            variants={{ show: { transition: stagger } }}
            initial="hidden"
            animate="show"
            className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden"
          >
            {filteredCourses.map((course) => (
              <CourseRow
                key={course.id}
                course={course}
                onClick={() => navigate(`/classes/${course.id}`)}
                onDelete={() => setDeleteTarget(course)}
              />
            ))}
          </motion.div>
        )}
      </div>

      <CreateCourseDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        courseName={deleteTarget?.name || ""}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </LayoutGroup>
  );
}
